import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { Category, MenuItem, OrderItem, Table, Order, Payment, SmartpayTerminal, IntegrationSettings } from '../types';
import { getAvailableQuantity } from '../lib/dailyAvailability';
import {
  filterCategoriesForChannel,
  filterMenuItemsForChannel,
} from '../lib/menuChannelAvailability';
import { sendPaymentToTerminal } from '../lib/smartconnect';
import MinusIcon from '../components/icons/MinusIcon';
import PlusIcon from '../components/icons/PlusIcon';
import ShoppingCartIcon from '../components/icons/ShoppingCartIcon';
import XCircleIcon from '../components/icons/XCircleIcon';
import ArrowLeftIcon from '../components/icons/ArrowLeftIcon';
import CheckCircleIcon from '../components/icons/CheckCircleIcon';
import CreditCardIcon from '../components/icons/CreditCardIcon';
import StarSolidIcon from '../components/icons/StarSolidIcon';
import MeatOptionModal from '../components/pos/MeatOptionModal';
import ItemNoteModal from '../components/pos/ItemNoteModal';
import TipInput from '../components/payments/TipInput';
import MenuItemImage from '../components/menu/MenuItemImage';
import { orderMatchesFloorPlanTable } from '../lib/orderUtils';

interface CustomerMenuPageProps {
  table: Table;
  menuItems: MenuItem[];
  categories: Category[];
  orders: Order[];
  onPlaceOrder: (items: OrderItem[], notes?: string) => void;
  onUpdateOrder?: (order: Order) => void;
  onProcessPayment?: (orderId: string, payment: Payment) => void;
  onExit: () => void;
  onUpdateMenuItem?: (itemId: number, updates: Partial<MenuItem>) => Promise<void>;
  terminals?: SmartpayTerminal[];
  integrationSettings?: IntegrationSettings;
}

const CustomerMenuPage: React.FC<CustomerMenuPageProps> = ({ table, menuItems, categories, orders, onPlaceOrder, onUpdateOrder, onProcessPayment, onExit, onUpdateMenuItem, terminals = [], integrationSettings }) => {
  const [view, setView] = useState<'menu' | 'review' | 'payment' | 'confirmation'>('menu');
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(categories.length > 0 ? categories[0].id : null);
  const [meatOptionModalItem, setMeatOptionModalItem] = useState<MenuItem | null>(null);
  const [existingOrder, setExistingOrder] = useState<Order | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'counter' | null>(null);
  const [itemNoteModal, setItemNoteModal] = useState<{isOpen: boolean, itemId: number, itemName: string, selectedMeatOption?: string, currentNote?: string}>({isOpen: false, itemId: 0, itemName: ''});
  const [cardPaymentStatus, setCardPaymentStatus] = useState<'idle' | 'processing' | 'approved' | 'declined'>('idle');
  const [cardPaymentMessage, setCardPaymentMessage] = useState('');
  const [selectedTerminalId, setSelectedTerminalId] = useState<string>(terminals[0]?.id || '');
  const [cardTipAmount, setCardTipAmount] = useState(0);
  const isInitialLoad = useRef(true);
  const lastOrderItemsHash = useRef<string>('');

  const visibleCategories = useMemo(
    () => filterCategoriesForChannel(categories, menuItems, 'qr'),
    [categories, menuItems]
  );

  const channelVisibleMenuItems = useMemo(
    () => filterMenuItemsForChannel(menuItems, categories, 'qr'),
    [menuItems, categories]
  );

  useEffect(() => {
    if (visibleCategories.length === 0) return;
    setSelectedCategoryId((prev) => {
      if (prev != null && visibleCategories.some((c) => String(c.id) === String(prev))) {
        return prev;
      }
      return visibleCategories[0].id;
    });
  }, [visibleCategories]);

  // Check for existing unpaid order for this table on mount
  useEffect(() => {
    const existingUnpaidOrder = orders.find(
      o => orderMatchesFloorPlanTable(o, table.id) && 
      o.paymentStatus === 'unpaid' && 
      o.orderType === 'dine-in'
    );
    
    if (existingUnpaidOrder) {
      // Create a hash of order items to detect changes
      const orderItemsHash = JSON.stringify(existingUnpaidOrder.items.map(i => ({
        id: i.id,
        qty: i.quantity,
        meat: i.selectedMeatOption
      })).sort((a, b) => a.id - b.id));
      
      // On initial load or different order, always sync
      if (isInitialLoad.current || !existingOrder || existingOrder.id !== existingUnpaidOrder.id) {
      setExistingOrder(existingUnpaidOrder);
        setOrderItems([...existingUnpaidOrder.items]);
      setNotes(existingUnpaidOrder.notes || '');
        lastOrderItemsHash.current = orderItemsHash;
        isInitialLoad.current = false;
      } else if (existingOrder.id === existingUnpaidOrder.id) {
        // Same order - only sync if the order items actually changed in the database
        // AND the change is significant (more items, not just quantity updates)
        if (orderItemsHash !== lastOrderItemsHash.current) {
          const localItemCount = orderItems.reduce((sum, item) => sum + item.quantity, 0);
          const orderItemCount = existingUnpaidOrder.items.reduce((sum, item) => sum + item.quantity, 0);
          
          // Only sync if order has more items than local (was updated externally)
          // Don't overwrite if user is actively adding items (local has more or equal)
          if (orderItemCount > localItemCount) {
            // Order was updated externally - merge items intelligently
            const mergedItems = [...orderItems];
            existingUnpaidOrder.items.forEach(orderItem => {
              const localItemIndex = mergedItems.findIndex(li => 
                li.id === orderItem.id && 
                li.selectedMeatOption === orderItem.selectedMeatOption
              );
              if (localItemIndex === -1) {
                // Item exists in order but not in local state - add it
                mergedItems.push(orderItem);
              } else if (orderItem.quantity > mergedItems[localItemIndex].quantity) {
                // Order has higher quantity - update it
                mergedItems[localItemIndex] = orderItem;
              }
            });
            
            setOrderItems(mergedItems);
            lastOrderItemsHash.current = JSON.stringify(mergedItems.map(i => ({
              id: i.id,
              qty: i.quantity,
              meat: i.selectedMeatOption
            })).sort((a, b) => a.id - b.id));
          }
          // Always update the order reference
          setExistingOrder(existingUnpaidOrder);
        }
      }
    } else {
      // No existing order - clear state only if we had an order before
      if (existingOrder) {
      setExistingOrder(null);
      setOrderItems([]);
      setNotes('');
        lastOrderItemsHash.current = '';
      }
      isInitialLoad.current = false;
    }
  }, [table.id, orders]);

  useEffect(() => {
    if (view === 'confirmation') {
      const timer = setTimeout(() => {
        onExit();
      }, 3000);
      return () => clearTimeout(timer);
    }
    // Reset payment method and card payment status when leaving payment view
    if (view !== 'payment') {
      setPaymentMethod(null);
      setCardPaymentStatus('idle');
      setCardPaymentMessage('');
      setCardTipAmount(0);
    }
  }, [view, onExit]);

  // Reset card payment status when payment method changes
  useEffect(() => {
    if (paymentMethod !== 'card') {
      setCardPaymentStatus('idle');
      setCardPaymentMessage('');
    }
  }, [paymentMethod]);

  const totalItems = orderItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  // Calculate balance - if previous order is paid, only show new items total
  // Otherwise show full total
  const calculateBalance = () => {
    if (!existingOrder || existingOrder.paymentStatus !== 'paid') {
      return totalPrice; // Full total if no order or unpaid
    }
    
    // Previous order is paid - calculate only new items
    const batches = existingOrder.orderBatches || [];
    const coveredIndices = new Set<number>();
    batches.forEach(batch => {
      batch.itemIndices.forEach(idx => {
        if (idx < orderItems.length) {
          coveredIndices.add(idx);
        }
      });
    });
    
    // Sum only uncovered items (new additions)
    const newItemsTotal = orderItems.reduce((sum, item, index) => {
      if (!coveredIndices.has(index)) {
        return sum + (item.price * item.quantity);
      }
      return sum;
    }, 0);
    
    return newItemsTotal;
  };
  
  const balance = calculateBalance();
  const previousOrderPaid = existingOrder?.paymentStatus === 'paid';
  
  // Check if previous order had "pay at counter" preference
  // This could be stored in notes or we can infer from payment method
  // For now, we'll check if there's a note or if payment was cash/counter
  const previousPaymentMethod = existingOrder?.payments && existingOrder.payments.length > 0 
    ? (existingOrder.payments[existingOrder.payments.length - 1].method === 'cash' ? 'counter' : 'card')
    : null;

  // Set default payment method if previous order had "pay at counter"
  // This ensures new items are appended to the same payment preference
  useEffect(() => {
    if (view === 'payment' && previousOrderPaid && previousPaymentMethod === 'counter' && !paymentMethod) {
      setPaymentMethod('counter');
    }
  }, [view, previousOrderPaid, previousPaymentMethod, paymentMethod]);

  const handleAddItem = (itemToAdd: MenuItem) => {
    // Check if item has meat options
    if (itemToAdd.meatOptions && itemToAdd.meatOptions.length > 0) {
      // Show meat option modal
      setMeatOptionModalItem(itemToAdd);
    } else {
      // Add directly without meat option
      addItemToOrder(itemToAdd, undefined);
    }
  };

  const addItemToOrder = (itemToAdd: MenuItem, selectedMeatOption?: string) => {
    // Calculate quantity being added
    const existingItem = orderItems.find(item => 
      item.id === itemToAdd.id && 
      item.selectedMeatOption === selectedMeatOption
    );
    const quantityToAdd = existingItem ? existingItem.quantity + 1 : 1;
    
    // Check daily availability using reserved quantity calculation
    if (itemToAdd.hasDailyAvailability) {
      const availableQuantity = getAvailableQuantity(itemToAdd, orders);
      if (availableQuantity <= 0) {
        alert('This item is currently finished for today.');
        return;
      }
      if (quantityToAdd > availableQuantity) {
        alert(`Only ${availableQuantity} plate(s) available. Cannot add ${quantityToAdd} to order.`);
        return;
      }
    }
    
    // Do NOT update menu item database when adding to cart - reservation happens when order is placed
    setOrderItems(prevItems => {
      // Check if item already exists with same meat option
      const existingItem = prevItems.find(item => 
        item.id === itemToAdd.id && 
        item.selectedMeatOption === selectedMeatOption
      );
      
      if (existingItem) {
        return prevItems.map(item =>
          item.id === itemToAdd.id && item.selectedMeatOption === selectedMeatOption
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      
      // Add new item with meat option if selected
      return [...prevItems, { 
        ...itemToAdd, 
        quantity: 1, 
        status: 'pending',
        selectedMeatOption: selectedMeatOption
      }];
    });
  };

  const handleMeatOptionSelected = (item: MenuItem, selectedOption: string) => {
    addItemToOrder(item, selectedOption);
    setMeatOptionModalItem(null);
  };

  const handleUpdateItemNote = (itemId: number, note: string, selectedMeatOption?: string) => {
    setOrderItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId && item.selectedMeatOption === selectedMeatOption
          ? { ...item, notes: note || undefined }
          : item
      )
    );
  };

  const handleOpenItemNoteModal = (item: OrderItem | MenuItem, isMenuItem: boolean = false, selectedMeatOption?: string) => {
    if (isMenuItem) {
      // For menu items, find if it's already in cart with same meat option
      const existingItem = orderItems.find(oi => 
        oi.id === item.id && 
        oi.selectedMeatOption === selectedMeatOption
      );
      setItemNoteModal({
        isOpen: true,
        itemId: item.id,
        itemName: item.name,
        selectedMeatOption: selectedMeatOption,
        currentNote: existingItem?.notes
      });
    } else {
      // For order items in review
      setItemNoteModal({
        isOpen: true,
        itemId: item.id,
        itemName: item.name,
        selectedMeatOption: (item as OrderItem).selectedMeatOption,
        currentNote: (item as OrderItem).notes
      });
    }
  };

  const handleSaveItemNote = (note: string) => {
    if (itemNoteModal.itemId) {
      // Check if item is in cart
      const existingItem = orderItems.find(oi => 
        oi.id === itemNoteModal.itemId && 
        oi.selectedMeatOption === itemNoteModal.selectedMeatOption
      );
      
      if (existingItem) {
        // Update existing item note
        handleUpdateItemNote(itemNoteModal.itemId, note, itemNoteModal.selectedMeatOption);
      } else {
        // Item not in cart yet - add it with note
        const menuItem = menuItems.find(mi => mi.id === itemNoteModal.itemId);
        if (menuItem) {
          addItemToOrder(menuItem, itemNoteModal.selectedMeatOption);
          // Then update the note
          setTimeout(() => {
            handleUpdateItemNote(itemNoteModal.itemId, note, itemNoteModal.selectedMeatOption);
          }, 100);
        }
      }
    }
    setItemNoteModal({ isOpen: false, itemId: 0, itemName: '' });
  };

  const handleUpdateQuantity = async (itemId: number, newQuantity: number, selectedMeatOption?: string) => {
    const item = menuItems.find(mi => mi.id === itemId);
    const orderItem = orderItems.find(oi => oi.id === itemId && oi.selectedMeatOption === selectedMeatOption);
    
    if (!item || !orderItem) return;
    
    const quantityChange = newQuantity - orderItem.quantity;
    
    // Check availability if increasing quantity
    if (quantityChange > 0 && item.hasDailyAvailability) {
      const currentAvailability = item.dailyAvailability ?? 0;
      if (newQuantity > currentAvailability) {
        alert(`Only ${currentAvailability} plate(s) available. Cannot set quantity to ${newQuantity}.`);
        return;
      }
    }
    
    // Update availability if item has daily availability
    if (item.hasDailyAvailability && onUpdateMenuItem && quantityChange !== 0) {
      const newAvailability = (item.dailyAvailability ?? 0) - quantityChange;
      try {
        await onUpdateMenuItem(itemId, {
          dailyAvailability: Math.max(0, newAvailability),
        });
      } catch (error) {
        console.error('Failed to update menu item availability:', error);
        alert('Failed to update availability. Please try again.');
        return;
      }
    }
    
    if (newQuantity <= 0) {
      setOrderItems(prev => prev.filter(item => 
        !(item.id === itemId && item.selectedMeatOption === selectedMeatOption)
      ));
    } else {
      setOrderItems(prev => prev.map(item => 
        item.id === itemId && item.selectedMeatOption === selectedMeatOption
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const handleFinalizeOrder = () => {
    if (orderItems.length === 0 || isProcessing) return;
    
    // If order is served or previous order is paid, payment method is required
    const isOrderServed = existingOrder?.status === 'served';
    if ((isOrderServed || previousOrderPaid) && !paymentMethod) {
      alert('Please select a payment method');
      return;
    }
    
    // If previous order is paid and balance is 0, nothing to pay
    if (previousOrderPaid && balance <= 0) {
      alert('No balance due. All items are already paid.');
      return;
    }
    
    setIsProcessing(true);
    
    // If there's an existing order, update it instead of creating a new one
    if (existingOrder && onUpdateOrder) {
      const updatedOrder: Order = {
        ...existingOrder,
        items: orderItems,
        notes: notes || undefined,
        // Maintain the current status (new/preparing) so it shows in kitchen display
        // Only reset to 'new' if order was already served/completed
        status: existingOrder.status === 'served' || existingOrder.status === 'comped' ? 'new' : existingOrder.status,
      };
      
      // Handle payment based on previous order status and selected method
      if (previousOrderPaid) {
        // Previous order is paid - handle based on payment method
        if (paymentMethod === 'card' && onProcessPayment && balance > 0) {
          // Card payment - process payment immediately for balance only
          // This will mark the order as paid again, resetting for next time
          // Note: For now, we'll process it. In future, could integrate terminal here
          const payment: Payment = {
            method: 'card',
            amount: balance,
            timestamp: Date.now(),
          };
          // Process payment for the balance
          onProcessPayment(existingOrder.id, payment);
          // The order will be updated with the payment, marking it as paid
        } else if (paymentMethod === 'counter') {
          // Pay at counter - append new items to existing order
          // The order will be paid together at counter (full total, not just balance)
          // The order remains unpaid until processed at counter
          // This allows accumulation - all items will be paid together at counter
        }
      } else if (isOrderServed) {
        // Order is served but not paid - handle payment
        if (paymentMethod === 'counter') {
          // Mark for counter payment - this will be handled at POS
        } else if (paymentMethod === 'card') {
          // Card payment - for now, show message that it will be processed at counter
          alert('Card payment will be processed at the counter. Please proceed to pay.');
        }
      }
      
      setTimeout(() => {
        onUpdateOrder(updatedOrder);
        setIsProcessing(false);
        setView('confirmation');
        setPaymentMethod(null);
        // Don't clear existing order state - keep it for potential future additions
      }, 1500);
    } else {
      // Create new order
      setTimeout(() => {
        onPlaceOrder(orderItems, notes);
        setIsProcessing(false);
        setView('confirmation');
        setPaymentMethod(null);
      }, 1500);
    }
  };

  const renderMenu = () => {
    const displayedItems = selectedCategoryId !== null && selectedCategoryId !== ''
      ? channelVisibleMenuItems.filter(
          (item) => String(item.categoryId) === String(selectedCategoryId)
        )
      : channelVisibleMenuItems;
      
    return (
    <>
      <header className="bg-white shadow-md p-4 sticky top-0 z-20">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-pink-500">KINGS PARK</h1>
            <p className="font-semibold text-gray-700">
              {existingOrder ? `Adding to existing order for ${table.name}` : `Ordering for ${table.name}`}
            </p>
            {existingOrder && (
              <p className="text-xs text-orange-600 mt-1">
                You have an active order. New items will be added to it.
              </p>
            )}
          </div>
          <button onClick={onExit} className="text-gray-500 hover:text-gray-800">
            <XCircleIcon className="w-8 h-8" />
          </button>
        </div>
        <div className="mt-4 overflow-x-auto pb-2 -mb-2">
          <nav className="flex space-x-4">
            {visibleCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategoryId(category.id)}
                className={`whitespace-nowrap py-2 px-3 rounded-full font-medium text-sm transition-colors ${
                  String(selectedCategoryId) === String(category.id)
                    ? 'bg-pink-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="flex-1 p-4 overflow-y-auto pb-32">
        <div className="grid grid-cols-1 gap-4">
          {displayedItems.map(item => {
              const orderItem = orderItems.find(oi => oi.id === item.id);
              const availableQuantity = item.hasDailyAvailability ? getAvailableQuantity(item, orders) : undefined;
              const isFinished = item.hasDailyAvailability && (availableQuantity ?? 0) <= 0;
              const isAvailable = item.hasDailyAvailability && (availableQuantity ?? 0) > 0;
              return(
              <div key={item.id} className={`bg-white rounded-lg shadow flex items-start p-3 relative ${isFinished ? 'opacity-60' : ''}`}>
                {item.isSignatureDish && (
                  <div className="absolute top-2 right-2 bg-gradient-to-br from-yellow-400 to-yellow-500 text-white p-1 rounded-full z-10 shadow-sm" title="Signature Dish">
                    <StarSolidIcon className="w-4 h-4" />
                  </div>
                )}
                <MenuItemImage src={item.imageUrlThumb || item.imageUrl || `https://picsum.photos/seed/${item.id}/200`} alt={item.name} className={`w-24 h-24 rounded-md mr-4 flex-shrink-0 ${isFinished ? 'grayscale' : ''}`} width={96} height={96} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 truncate">{item.name}</h3>
                      {item.description && (
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <div className="ml-2 flex flex-col items-end space-y-1">
                      {isFinished && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-bold rounded">Finished</span>
                      )}
                      {isAvailable && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-bold rounded">
                          {availableQuantity} available
                        </span>
                      )}
                    </div>
                  </div>
                  {item.meatOptions && item.meatOptions.length > 0 && (
                    <p className="text-xs text-pink-600 mb-1">Multiple options available</p>
                  )}
                  <p className="text-gray-600 mb-2">${item.price.toFixed(2)}</p>
                  {orderItem ? (
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleUpdateQuantity(item.id, orderItem.quantity - 1, orderItem.selectedMeatOption)} className="p-2 rounded-full bg-gray-200 text-gray-700"><MinusIcon className="w-4 h-4" /></button>
                      <span className="px-2 w-10 text-center font-semibold text-gray-900">{orderItem.quantity}</span>
                      <button onClick={() => handleUpdateQuantity(item.id, orderItem.quantity + 1, orderItem.selectedMeatOption)} className="p-2 rounded-full bg-gray-200 text-gray-700"><PlusIcon className="w-4 h-4" /></button>
                      <button 
                        onClick={() => handleOpenItemNoteModal(orderItem, false)}
                        className="p-2 rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors"
                        title={orderItem.notes ? 'Edit note' : 'Add note'}
                      >
                        <span className="text-sm">📝</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleAddItem(item)} 
                      disabled={isFinished}
                        className={`font-semibold py-1 px-3 rounded-full text-sm flex-1 ${
                        isFinished
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-pink-100 text-pink-600 hover:bg-pink-200'
                      }`}
                    >
                      {isFinished ? 'Finished' : 'Add to Order'}
                    </button>
                      <button 
                        onClick={() => {
                          // If item has meat options, need to select meat option first
                          if (item.meatOptions && item.meatOptions.length > 0) {
                            setMeatOptionModalItem(item);
                            // Store that we want to add note after meat selection
                            // We'll handle this after meat is selected
                          } else {
                            handleOpenItemNoteModal(item, true);
                          }
                        }}
                        className="p-2 rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors"
                        title="Add special instructions"
                      >
                        <span className="text-sm">📝</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {totalItems > 0 && (
        <div className="fixed bottom-6 right-6 z-30">
          <button
            onClick={() => setView('review')}
            className="bg-pink-500 text-white rounded-full shadow-lg p-4 hover:bg-pink-600 transition-colors flex items-center"
          >
            <ShoppingCartIcon className="w-6 h-6" />
            <span className="ml-2 font-bold">{totalItems}</span>
            <span className="mx-2">|</span>
            <span className="font-bold">${totalPrice.toFixed(2)}</span>
          </button>
        </div>
      )}
    </>
  )};

  const renderReview = () => {
    // Group items by batches if order has batches
    const batches = existingOrder?.orderBatches || [];
    const hasBatches = batches.length > 0;
    
    // If no batches, show all items as one group
    const renderItem = (item: OrderItem, index: number) => (
          <div key={`${item.id}-${item.selectedMeatOption || 'default'}-${index}`} className="mb-4 bg-white p-3 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{item.name}</p>
                {item.selectedMeatOption && (
                  <p className="text-xs text-pink-600">({item.selectedMeatOption})</p>
                )}
                <p className="text-sm text-gray-500">${item.price.toFixed(2)}</p>
              </div>
          <div className="flex items-center gap-2">
              <button onClick={() => {
                const itemToUpdate = orderItems.find(oi => 
                  oi.id === item.id && 
                  oi.selectedMeatOption === item.selectedMeatOption
                );
                if (itemToUpdate) {
                handleUpdateQuantity(item.id, item.quantity - 1, item.selectedMeatOption);
                }
              }} className="p-2 rounded-full bg-gray-200 text-gray-700"><MinusIcon className="w-4 h-4" /></button>
              <span className="px-3 font-semibold text-gray-900">{item.quantity}</span>
              <button onClick={() => {
                const itemToUpdate = orderItems.find(oi => 
                  oi.id === item.id && 
                  oi.selectedMeatOption === item.selectedMeatOption
                );
                if (itemToUpdate) {
                handleUpdateQuantity(item.id, item.quantity + 1, item.selectedMeatOption);
                }
              }} className="p-2 rounded-full bg-gray-200 text-gray-700"><PlusIcon className="w-4 h-4" /></button>
            <button 
              onClick={() => handleOpenItemNoteModal(item, false)}
              className={`p-2 rounded-full transition-colors ${
                item.notes 
                  ? 'bg-yellow-200 text-yellow-800' 
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
              title={item.notes ? 'Edit note' : 'Add note'}
            >
              <span className="text-sm">📝</span>
            </button>
            </div>
            <p className="w-20 text-right font-semibold text-gray-900">${(item.price * item.quantity).toFixed(2)}</p>
            </div>
            {/* Display item notes if present - highlighted in noticeable color */}
            {item.notes && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-3 py-2 shadow-sm">
                  <p className="text-xs font-bold text-yellow-900 mb-1 flex items-center">
                    <span className="mr-1">📝</span>
                    Special Instructions:
                  </p>
                  <p className="text-sm font-medium text-yellow-900">{item.notes}</p>
                </div>
              </div>
            )}
          </div>
    );

    const formatBatchTime = (timestamp: number) => {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
      <div className="absolute inset-0 bg-gray-50 flex flex-col z-40">
        <header className="bg-white shadow-md p-4 flex-shrink-0 flex items-center">
          <button onClick={() => setView('menu')} className="text-gray-600 hover:text-gray-900 mr-4">
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <h2 className="text-xl font-bold text-gray-800">Review Your Order</h2>
        </header>
        <main className="flex-1 overflow-y-auto p-4">
          {hasBatches ? (
            <>
              {/* Display items grouped by batches */}
              {batches.map((batch, batchIndex) => {
                const batchItems = batch.itemIndices
                  .filter(idx => idx < orderItems.length)
                  .map(idx => ({ item: orderItems[idx], originalIndex: idx }));
                
                const batchTotal = batchItems.reduce((sum, { item }) => sum + (item.price * item.quantity), 0);
                const isFirstBatch = batchIndex === 0;
                
                return (
                  <div key={batch.batchId} className="mb-6">
                    <div className="mb-3 pb-2 border-b-2 border-gray-300">
                      <h3 className="text-lg font-bold text-gray-800">
                        {isFirstBatch ? 'Original Order' : `Additional Items #${batchIndex}`}
                      </h3>
                      <p className="text-xs text-gray-500">Added at {formatBatchTime(batch.timestamp)}</p>
                    </div>
                    {batchItems.map(({ item, originalIndex }) => renderItem(item, originalIndex))}
                    <div className="mt-2 text-right">
                      <span className="text-sm text-gray-600">Batch Subtotal: </span>
                      <span className="font-semibold text-gray-900">${batchTotal.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
              
              {/* Show any items that aren't in any batch (newly added items not yet saved) */}
              {(() => {
                // Get all indices that are covered by batches
                const coveredIndices = new Set<number>();
                batches.forEach(batch => {
                  batch.itemIndices.forEach(idx => {
                    if (idx < orderItems.length) {
                      coveredIndices.add(idx);
                    }
                  });
                });
                
                // Find items that aren't covered by any batch
                const uncoveredItems: Array<{ item: OrderItem; index: number }> = [];
                orderItems.forEach((item, index) => {
                  if (!coveredIndices.has(index)) {
                    uncoveredItems.push({ item, index });
                  }
                });
                
                // If there are uncovered items, show them as a new batch
                if (uncoveredItems.length > 0) {
                  const uncoveredTotal = uncoveredItems.reduce((sum, { item }) => sum + (item.price * item.quantity), 0);
                  return (
                    <div key="uncovered-items" className="mb-6">
                      <div className="mb-3 pb-2 border-b-2 border-gray-300">
                        <h3 className="text-lg font-bold text-gray-800">
                          {batches.length === 0 ? 'Original Order' : `Additional Items #${batches.length}`}
                        </h3>
                        <p className="text-xs text-gray-500">Added just now</p>
                      </div>
                      {uncoveredItems.map(({ item, index }) => renderItem(item, index))}
                      <div className="mt-2 text-right">
                        <span className="text-sm text-gray-600">Batch Subtotal: </span>
                        <span className="font-semibold text-gray-900">${uncoveredTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </>
          ) : (
            // No batches - show all items as one list
            orderItems.map((item, index) => renderItem(item, index))
          )}
          
        <div className="mt-6">
          <label htmlFor="orderNotes" className="block text-sm font-medium text-gray-700">Special Instructions</label>
          <textarea
            id="orderNotes" value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={3} placeholder="e.g., allergies, extra sauce..."
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
          />
        </div>
      </main>
      <footer className="bg-white border-t p-4">
        <div className="flex justify-between items-center mb-4">
            {previousOrderPaid ? (
              <>
                <div>
                  <span className="text-lg font-semibold">Balance Due:</span>
                  <p className="text-xs text-gray-500 mt-1">Previous order paid</p>
                </div>
                <span className="text-2xl font-bold">${balance.toFixed(2)}</span>
              </>
            ) : (
              <>
          <span className="text-lg font-semibold">Total:</span>
          <span className="text-2xl font-bold">${totalPrice.toFixed(2)}</span>
              </>
            )}
        </div>
        <button onClick={() => setView('payment')} className="w-full bg-pink-500 text-white font-bold py-3 rounded-lg hover:bg-pink-600 transition-colors">
          Proceed to Payment
        </button>
      </footer>
    </div>
  );
  };

  const renderPayment = () => {
    const isOrderServed = existingOrder?.status === 'served';
    const canPayAfterMeal = isOrderServed;
    const showPaymentOptions = !existingOrder || canPayAfterMeal || existingOrder.paymentStatus === 'unpaid';
    
    return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col z-50">
       <header className="bg-white shadow-md p-4 flex-shrink-0 flex items-center">
        <button onClick={() => setView('review')} className="text-gray-600 hover:text-gray-900 mr-4">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <h2 className="text-xl font-bold text-gray-800">Payment</h2>
      </header>
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-center mb-6">
          <CreditCardIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              {previousOrderPaid ? (
                <>
                  {previousPaymentMethod === 'counter' ? (
                    <>
                      <p className="text-gray-600 mb-1">Total Amount:</p>
                      <p className="text-4xl font-bold text-gray-900 mb-2">${totalPrice.toFixed(2)}</p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-xs text-blue-800 font-semibold mb-1">Previous payment: Pay at Counter</p>
                        <p className="text-xs text-blue-700">New items will be added to your counter payment. Total due at counter: ${totalPrice.toFixed(2)}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-600 mb-1">Balance Due:</p>
                      <p className="text-4xl font-bold text-gray-900 mb-2">${balance.toFixed(2)}</p>
                      <p className="text-sm text-gray-500 mb-2">Previous order: Paid (Card)</p>
                      <p className="text-xs text-gray-500 mb-4">Only new items will be charged</p>
                    </>
                  )}
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-1">Total Amount:</p>
                  <p className="text-4xl font-bold text-gray-900 mb-4">${totalPrice.toFixed(2)}</p>
                </>
              )}
            </div>

            {showPaymentOptions && (
              <>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Select Payment Method</label>
                  <div className="space-y-3">
                    <button
                      onClick={() => setPaymentMethod('card')}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                        paymentMethod === 'card'
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center">
                        <CreditCardIcon className="w-6 h-6 mr-3 text-gray-600" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">Pay with Card</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {canPayAfterMeal 
                              ? 'Pay now using card terminal'
                              : 'Pay at counter with card after your meal'}
                          </p>
                        </div>
                        {paymentMethod === 'card' && (
                          <CheckCircleIcon className="w-6 h-6 text-pink-500" />
                        )}
                      </div>
                    </button>

                    <button
                      onClick={() => setPaymentMethod('counter')}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                        paymentMethod === 'counter'
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-6 h-6 mr-3 flex items-center justify-center">
                          <span className="text-2xl">💵</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">Pay at Counter</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {canPayAfterMeal 
                              ? 'Pay with cash or card at the counter'
                              : 'Pay at counter when you are ready to leave'}
                          </p>
                        </div>
                        {paymentMethod === 'counter' && (
                          <CheckCircleIcon className="w-6 h-6 text-pink-500" />
                        )}
                      </div>
                    </button>
                  </div>
                </div>

                {!canPayAfterMeal && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Your order will be sent to the kitchen. You can pay after your meal is served.
                    </p>
                  </div>
                )}

                {/* Card Payment Section */}
                {paymentMethod === 'card' && (canPayAfterMeal || previousOrderPaid) && (
                  <div className="mb-6 p-4 bg-white border-2 border-pink-200 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <CreditCardIcon className="w-5 h-5 mr-2 text-pink-500" />
                      Card Payment
                    </h3>
                    
                    {terminals.length > 0 ? (
                      <>
                        {terminals.length > 1 && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Terminal
                            </label>
                            <select 
                              value={selectedTerminalId} 
                              onChange={(e) => setSelectedTerminalId(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                              disabled={cardPaymentStatus === 'processing'}
                            >
                              {terminals.map(t => (
                                <option key={t.id} value={t.id}>
                                  {t.name} {!t.isPaired ? '(Not Paired)' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {cardPaymentStatus === 'idle' && (
                          <>
                            <TipInput
                              baseAmount={previousOrderPaid ? balance : totalPrice}
                              tipAmount={cardTipAmount}
                              onTipChange={setCardTipAmount}
                              className="mb-4"
                            />
                            <button
                              onClick={async () => {
                                const amountToCharge = previousOrderPaid ? balance : totalPrice;
                                const totalWithTip = amountToCharge + cardTipAmount;
                                const terminal = terminals.find(t => t.id === selectedTerminalId);
                                
                                if (!terminal) {
                                  alert("Please select a valid payment terminal.");
                                  return;
                                }
                                
                                if (!terminal.isPaired) {
                                  alert("This terminal is not paired. Please pair it in Settings first.");
                                  return;
                                }
                                
                                setCardPaymentStatus('processing');
                                setCardPaymentMessage(`Please follow instructions on ${terminal.name}...`);
                                
                                try {
                                  let delayedShown = false;
                                  const onDelayed = () => {
                                    if (!delayedShown) {
                                      delayedShown = true;
                                      setCardPaymentMessage('Transaction is taking longer than usual. Please wait...');
                                    }
                                  };

                                  // Send total amount (payment + tip) to terminal
                                  const result = await sendPaymentToTerminal(
                                    terminal,
                                    totalWithTip,
                                    existingOrder || undefined,
                                    integrationSettings?.businessInfo,
                                    undefined,
                                    undefined,
                                    onDelayed
                                  );
                                  
                                  setCardPaymentMessage(result.message);
                                  
                                  if (result.success) {
                                    setCardPaymentStatus('approved');
                                    if (onProcessPayment && existingOrder) {
                                      const payment: Payment = {
                                        method: 'card',
                                        amount: amountToCharge,
                                        tip: cardTipAmount > 0 ? cardTipAmount : undefined,
                                        timestamp: Date.now(),
                                        terminalId: terminal.registerId,
                                        transactionId: result.transactionId,
                                        registerId: terminal.registerId,
                                      };
                                      onProcessPayment(existingOrder.id, payment);
                                    }
                                    // Reset tip after successful payment
                                    setCardTipAmount(0);
                                    // Auto-close after success
                                    setTimeout(() => {
                                      handleFinalizeOrder();
                                    }, 2000);
                                  } else {
                                    setCardPaymentStatus('declined');
                                  }
                                } catch (error: any) {
                                  console.error('Payment error:', error);
                                  setCardPaymentStatus('declined');
                                  setCardPaymentMessage(`Error: ${error.message || 'Payment failed'}`);
                                }
                              }}
                              disabled={!selectedTerminalId || terminals.length === 0}
                              className="w-full bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed py-3 font-bold text-lg"
                            >
                              {cardTipAmount > 0 
                                ? `Process Payment $${(previousOrderPaid ? balance + cardTipAmount : totalPrice + cardTipAmount).toFixed(2)} (${(previousOrderPaid ? balance : totalPrice).toFixed(2)} + ${cardTipAmount.toFixed(2)} tip)`
                                : `Process Card Payment ($${previousOrderPaid ? balance.toFixed(2) : totalPrice.toFixed(2)})`
                              }
                            </button>
                          </>
                        )}

                        {cardPaymentStatus === 'processing' && (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mb-3"></div>
                            <p className="text-gray-700 font-medium">{cardPaymentMessage}</p>
                          </div>
                        )}

                        {cardPaymentStatus === 'approved' && (
                          <div className="text-center py-4 bg-green-50 border-2 border-green-500 rounded-lg">
                            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
                            <p className="text-green-800 font-bold text-lg">Payment Approved!</p>
                            <p className="text-green-700 text-sm mt-1">{cardPaymentMessage}</p>
                          </div>
                        )}

                        {cardPaymentStatus === 'declined' && (
                          <div className="text-center py-4 bg-red-50 border-2 border-red-500 rounded-lg mb-3">
                            <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-2" />
                            <p className="text-red-800 font-bold text-lg">Payment Declined</p>
                            <p className="text-red-700 text-sm mt-1">{cardPaymentMessage}</p>
                            <button
                              onClick={() => {
                                setCardPaymentStatus('idle');
                                setCardPaymentMessage('');
                              }}
                              className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                              Try Again
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      // Simulation mode when no terminals available
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-3">No payment terminals available. Using simulation mode.</p>
                        <button
                          onClick={async () => {
                            const amountToCharge = previousOrderPaid ? balance : totalPrice;
                            setCardPaymentStatus('processing');
                            setCardPaymentMessage('Simulating card payment...');
                            
                            // Simulate payment delay
                            await new Promise(resolve => setTimeout(resolve, 2000));
                            
                            // Simulate success (90% success rate for testing)
                            const success = Math.random() > 0.1;
                            
                            if (success) {
                              setCardPaymentStatus('approved');
                              setCardPaymentMessage('Payment simulated successfully');
                              if (onProcessPayment && existingOrder) {
                                const payment: Payment = {
                                  method: 'card',
                                  amount: amountToCharge,
                                  timestamp: Date.now(),
                                  transactionId: `SIM-${Date.now()}`,
                                };
                                onProcessPayment(existingOrder.id, payment);
                              }
                              setTimeout(() => {
                                handleFinalizeOrder();
                              }, 2000);
                            } else {
                              setCardPaymentStatus('declined');
                              setCardPaymentMessage('Payment simulation declined');
                            }
                          }}
                          className="w-full bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors py-3 font-bold text-lg"
                        >
                          {cardTipAmount > 0 
                            ? `Simulate Payment $${(previousOrderPaid ? balance + cardTipAmount : totalPrice + cardTipAmount).toFixed(2)} (${(previousOrderPaid ? balance : totalPrice).toFixed(2)} + ${cardTipAmount.toFixed(2)} tip)`
                            : `Simulate Card Payment ($${previousOrderPaid ? balance.toFixed(2) : totalPrice.toFixed(2)})`
                          }
                        </button>
                        
                        {cardPaymentStatus === 'processing' && (
                          <div className="text-center py-4 mt-3">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mb-3"></div>
                            <p className="text-gray-700 font-medium">{cardPaymentMessage}</p>
                          </div>
                        )}

                        {cardPaymentStatus === 'approved' && (
                          <div className="text-center py-4 mt-3 bg-green-50 border-2 border-green-500 rounded-lg">
                            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
                            <p className="text-green-800 font-bold text-lg">Payment Approved!</p>
                            <p className="text-green-700 text-sm mt-1">{cardPaymentMessage}</p>
                          </div>
                        )}

                        {cardPaymentStatus === 'declined' && (
                          <div className="text-center py-4 mt-3 bg-red-50 border-2 border-red-500 rounded-lg">
                            <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-2" />
                            <p className="text-red-800 font-bold text-lg">Payment Declined</p>
                            <p className="text-red-700 text-sm mt-1">{cardPaymentMessage}</p>
                            <button
                              onClick={() => {
                                setCardPaymentStatus('idle');
                                setCardPaymentMessage('');
                              }}
                              className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                              Try Again
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

           <button 
            onClick={handleFinalizeOrder} 
              disabled={
                isProcessing || 
                (canPayAfterMeal && !paymentMethod) || 
                (previousOrderPaid && previousPaymentMethod !== 'counter' && balance <= 0) ||
                (paymentMethod === 'card' && (canPayAfterMeal || previousOrderPaid) && cardPaymentStatus !== 'approved' && cardPaymentStatus !== 'idle')
              }
              className="w-full bg-green-500 text-white font-bold py-4 rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isProcessing 
                ? 'Processing...' 
                : previousOrderPaid && previousPaymentMethod !== 'counter' && balance <= 0
                  ? 'No Balance Due'
                  : (canPayAfterMeal || (previousOrderPaid && paymentMethod === 'card'))
                    ? paymentMethod === 'card' && cardPaymentStatus === 'approved'
                      ? 'Complete Order'
                      : paymentMethod === 'card'
                        ? 'Process Payment First'
                        : paymentMethod === 'counter'
                          ? 'Confirm Pay at Counter'
                          : 'Select Payment Method'
                    : paymentMethod
                      ? `Confirm Order (${paymentMethod === 'card' ? 'Card' : 'Pay at Counter'})`
                      : 'Confirm Order & Send to Kitchen'
              }
          </button>
        </div>
      </main>
    </div>
  );
  };
  
  const renderConfirmation = () => (
      <div className="absolute inset-0 bg-green-500 z-50 flex flex-col items-center justify-center text-white text-center p-8">
          <CheckCircleIcon className="w-24 h-24 mb-6" />
          <h2 className="text-4xl font-bold mb-2">Thank You!</h2>
          <p className="text-lg">Your order has been sent to the kitchen.</p>
          <p className="text-sm mt-4">This screen will close automatically.</p>
      </div>
  );

  return (
    <div className="h-screen w-screen bg-gray-50 font-sans flex flex-col max-w-lg mx-auto border-x relative overflow-hidden">
        <div style={{ display: view === 'menu' ? 'flex' : 'none' }} className="flex-col h-full w-full">
            {renderMenu()}
        </div>
        {view === 'review' && renderReview()}
        {view === 'payment' && renderPayment()}
        {view === 'confirmation' && renderConfirmation()}
        {meatOptionModalItem && (
          <MeatOptionModal
            item={meatOptionModalItem}
            onSelect={handleMeatOptionSelected}
            onCancel={() => setMeatOptionModalItem(null)}
          />
        )}
        {itemNoteModal.isOpen && (
          <ItemNoteModal
            isOpen={itemNoteModal.isOpen}
            itemName={itemNoteModal.itemName}
            currentNote={itemNoteModal.currentNote}
            onClose={() => setItemNoteModal({ isOpen: false, itemId: 0, itemName: '' })}
            onSave={handleSaveItemNote}
          />
        )}
    </div>
  );
};

export default CustomerMenuPage;